import { Box, Chip, CircularProgress, ListItem, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ON_FORM_CHANGE, SET_API_DATA } from "../../redux/constants";
import { toggleModelShow } from "../../shared/CustomFunc";
import { Brain, Calender, Lab, Provider, StrongHand } from "../../shared/icon";
import VSNButton from "../../shared/VSNButton";
import VSNModel from "../../shared/VSNModel";
import VSNTypography from "../../shared/VSNTypography";
import { ApiContainer } from "../../utils/api";
import EventCommunication from "./EventSchedule/EventCommunication";
import EventDetails from "./EventSchedule/EventDetails";
import { EventScheduleStyle } from "./EventSchedule/EventScheduleStyle";
import EventStatistics from "./EventSchedule/EventStatistics";
import EventStatusSchedule from "./EventStatusSchedule";

const EventScheduleTabs = () => {
  const classes = EventScheduleStyle();
  const { api } = ApiContainer();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const form = useSelector((state) => state?.form);
  const { eventId, organizationId, locationId, departmentId } = useParams();
  const eventScheduleToEdit = useSelector(
    (state) => state.api?.eventScheduleToEdit
  );
  const modelState = useSelector((state) => state?.modelStates);
  const createEventmodalLoaders = useSelector(
    (state) => state.api?.loader?.createEventmodal
  );
  const eventProgressTimeline = useSelector(
    (state) => state.api?.eventProgressTimeline
  );

  const [activeInnerTab, setActiveInnerTab] = useState("event_details");

  const innertab = [
    { id: 1, name: "Event Details", value: "event_details" },
    { id: 2, name: "Event Communication", value: "event_communication" },
    { id: 3, name: "Event Statistics", value: "event_statistics" },
  ];

  const renderContent = () => {
    switch (activeInnerTab) {
      case "event_details":
        return <EventDetails />;
      case "event_communication":
        return <EventCommunication />;
      case "event_statistics":
        return <EventStatistics />;
      default:
        return null;
    }
  };

  const handleEventProgressTimeline = async () => {
    setLoading(true);
    if (eventId) {
      const data = await api({
        method: "GET",
        endPoint: `organization/${organizationId}/locations/${locationId}/departments/${departmentId}/event-schedule/${eventId}/event-progress-timeline`,
        showToastMessage: false,
        urlencoded: false,
        needLoader: true,
        parent: "eventProgressTimeline",
        needFullResponse: false,
      });

      if (data?.status) {
        dispatch({
          type: SET_API_DATA,
          payload: {
            eventProgressTimeline: data?.data,
          },
        });
        setLoading(false);
      }
    }
  };

  const handelEventScheduleClick = async (row) => {
    const data = await api({
      method: "GET",
      endPoint: `organization/${organizationId}/locations/${locationId}/departments/${departmentId}/event-schedule`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "createEventmodal",
      needFullResponse: false,
    });
    if (data?.status) {
      dispatch({
        type: ON_FORM_CHANGE,
        payload: {
          formData: {
            ...form.formData,
            eventScheduleInfoStatus: data?.data,
          },
        },
      });
    }
    dispatch(toggleModelShow("EventStatusScheduleFromDepartment"));
  };

  useEffect(() => {
    if (eventId) {
      handleEventProgressTimeline();
    }
  }, [eventId]);

  const labelMapping = {
    event_created: {
      label: "Event Created",
      icon: <Calender />,
    },
    lab_draw: {
      label: "Lab Draw",
      icon: <Brain />,
    },
    online_assessments: {
      label: "Online Assessments",
      icon: <Lab />,
    },
    physical_assessment: {
      label: "Physical Assessment",
      icon: <StrongHand />,
    },
    provider_visit: {
      label: "Provider Visit",
      icon: <Provider />,
    },
  };

  return (
    <>
      <Box px={3}>
        <Stack direction={{ sm: "row" }} mb={3} justifyContent="space-between">
          <Stack className={classes.heading} mb={{ sm: 0, xs: 1 }}>
            <VSNTypography variant="h5" component="h5">
              {eventScheduleToEdit?.event_name ?? ""}
            </VSNTypography>
            <VSNTypography variant="body1" component="p" color="#3A3A40">
              Set event timelines, schedule communications, and view
              participation stats, all from one dashboard.
            </VSNTypography>
          </Stack>
          <Stack flex="none">
            <VSNButton
              sx={{ px: 3 }}
              handleClick={() => handelEventScheduleClick()}
              isLoading={createEventmodalLoaders}
            >
              View All Events
            </VSNButton>
          </Stack>
        </Stack>

        <Box className={classes.timelineWrapper}>
          <VSNTypography variant="h6" component="h6">
            Event Progress Timeline
          </VSNTypography>
          <VSNTypography variant="body1" component="p" color="#3A3A40" mb={3}>
            Track the current status and progress of your screening event
          </VSNTypography>
          <TimelineProgressBar
            loading={loading}
            data={eventProgressTimeline ?? {}}
            labelMapping={labelMapping}
          />
        </Box>

        <Stack
          direction="row"
          justifyContent="start"
          className={classes.innerTabsWrapper}
        >
          {innertab.map((data) => (
            <ListItem
              key={data.id}
              className={`${classes.listitemTab} ${
                activeInnerTab === data.value ? "active" : ""
              }`}
              onClick={() => setActiveInnerTab(data.value)}
            >
              <span>{data.name}</span>
            </ListItem>
          ))}
        </Stack>

        {renderContent()}
      </Box>

      {modelState?.EventStatusScheduleFromDepartment && (
        <VSNModel
          open={modelState?.EventStatusScheduleFromDepartment}
          onClose={() => {
            dispatch(toggleModelShow("EventStatusScheduleFromDepartment"));
          }}
          disableEnforceFocus
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          mobileDrawer
        >
          <EventStatusSchedule
            organizationId={organizationId}
            locationId={locationId}
            departmentId={departmentId}
            eventId={eventId}
          />
        </VSNModel>
      )}
    </>
  );
};

const TimelineProgressBar = ({ data, labelMapping, loading }) => {
  const classes = EventScheduleStyle();

  const getStatus = (dateStr) => {
    const today = dayjs().startOf("day");
    const date = dayjs(dateStr).startOf("day");

    if (date.isBefore(today)) return "completed";
    if (date.isSame(today)) return "active";
    return "upcoming";
  };

  const steps = Object.entries(data).map(([key, dateStr]) => ({
    label: labelMapping[key]?.label || key,
    icon: labelMapping[key]?.icon || null,
    date: dayjs(dateStr).format("MM/DD/YYYY"),
    status: getStatus(dateStr),
  }));

  if (loading) {
    return (
      <Box
        style={{
          height: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Stack direction="row" sx={{ overflowX: "auto" }}>
      {steps.map((step, index) => {
        return (
          <Box key={index} className={`${classes.timelineBox} ${step.status}`}>
            {index !== 0 && (
              <Box className={`${classes.stepLine} ${step.status}`} />
            )}

            <Box className={`${classes.timelineContent} ${step.status}`}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                {step.icon}
                <Chip label={step.status} />
              </Stack>
              <VSNTypography variant="body1" color="#3A3A40" fontWeight={500}>
                {step.label}
              </VSNTypography>
              <VSNTypography variant="body1" color="#3A3A40">
                {step.date}
              </VSNTypography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default EventScheduleTabs;
